// Intent: first-run coach guide, redrawn per the Stitch mocks
// (samouczek_i_nowe_kolko): a DIMMED Start screen with a SPOTLIGHT on the real
// element being explained (the "+", the pull area, the profile circle, the Siła
// prognozy badge) plus a callout card — not a centered text card (owner: "wróć
// do moków z przyciemnianiem"). Shown once after onboarding; replayable from
// Więcej. No emoji — text only.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets, type EdgeInsets } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { useAppMetaStore } from '@/store/app-meta-store';
import { colors, radius, spacing } from '@/theme/tokens';

type TargetKind = 'fab' | 'pull' | 'avatar' | 'score' | 'gift' | 'none';

const STEPS: { target: TargetKind; titleKey: string; bodyKey: string }[] = [
  { target: 'fab', titleKey: 'tutorial.addTitle', bodyKey: 'tutorial.addBody' },
  { target: 'pull', titleKey: 'tutorial.pullTitle', bodyKey: 'tutorial.pullBody' },
  { target: 'avatar', titleKey: 'tutorial.settingsTitle', bodyKey: 'tutorial.settingsBody' },
  { target: 'score', titleKey: 'tutorial.strengthTitle', bodyKey: 'tutorial.strengthBody' },
  // Referral (CEO ask): every client should know the gift icon = 50 zł voucher.
  { target: 'gift', titleKey: 'tutorial.referTitle', bodyKey: 'tutorial.referBody' },
  // End on the day-to-day habit (no specific element) — centered card.
  { target: 'none', titleKey: 'tutorial.habitTitle', bodyKey: 'tutorial.habitBody' },
];

const DIM = 'rgba(3, 24, 64, 0.74)';
const TAB_BAR_HEIGHT = 49; // mirrors fab.tsx

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  radius: number;
}

// Approximate on-screen box of each Start element (layout is fixed enough for a
// coach mark). Circles get a padding ring; the pull area is a wide rounded rect.
function boxFor(target: TargetKind, W: number, H: number, insets: EdgeInsets): Box | null {
  const P = 8;
  const circle = (cx: number, cy: number, r: number): Box => ({
    x: cx - r - P,
    y: cy - r - P,
    w: 2 * (r + P),
    h: 2 * (r + P),
    radius: r + P,
  });
  const headerCy = insets.top + spacing.screen + 20; // header row vertical centre
  switch (target) {
    case 'fab':
      return circle(W - spacing.screen - 28, H - (TAB_BAR_HEIGHT + insets.bottom + spacing.md) - 28, 28);
    case 'avatar':
      return circle(spacing.screen + 20, headerCy, 20);
    case 'score':
      return circle(spacing.screen + 40 + spacing.sm + 20, headerCy, 20);
    case 'gift':
      // Header right side: [bulb 40][gap][gift 40][gap][bell 40] + screen padding.
      return circle(W - spacing.screen - 40 - spacing.sm - 20, headerCy, 20);
    case 'pull':
      return { x: 12, y: insets.top + spacing.screen + 52, w: W - 24, h: 76, radius: 18 };
    default:
      return null;
  }
}

export function TutorialOverlay() {
  const { t } = useTranslation();
  const { width: W, height: H } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const hydrated = useAppMetaStore((s) => s.hydrated);
  const onboardingDone = useAppMetaStore((s) => s.onboardingDone);
  const tutorialDone = useAppMetaStore((s) => s.tutorialDone);
  const completeTutorial = useAppMetaStore((s) => s.completeTutorial);
  const [idx, setIdx] = useState(0);

  const visible = hydrated && onboardingDone && !tutorialDone;
  if (!visible) return null;

  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;
  const box = boxFor(step.target, W, H, insets);
  const close = () => {
    setIdx(0);
    void completeTutorial();
  };
  const next = () => (isLast ? close() : setIdx((i) => i + 1));

  // Callout sits opposite the spotlight so it never covers it. No box -> centre.
  const belowTarget = box ? box.y < H / 2 : false;
  const cardWrap = box
    ? belowTarget
      ? { top: box.y + box.h + spacing.md }
      : { bottom: H - box.y + spacing.md }
    : { top: H / 2 - 120 };

  return (
    <Modal transparent animationType="fade" visible onRequestClose={close}>
      {/* Base catch layer: blocks touches to the Start screen behind (so the
          spotlighted control isn't accidentally triggered mid-tour). */}
      <Pressable style={StyleSheet.absoluteFill} onPress={() => {}} />

      {/* Dim: a full sheet, or a 4-rect frame that leaves a clear hole. */}
      {box ? (
        <>
          <View pointerEvents="none" style={{ position: 'absolute', left: 0, right: 0, top: 0, height: box.y, backgroundColor: DIM }} />
          <View pointerEvents="none" style={{ position: 'absolute', left: 0, right: 0, top: box.y + box.h, bottom: 0, backgroundColor: DIM }} />
          <View pointerEvents="none" style={{ position: 'absolute', left: 0, top: box.y, width: box.x, height: box.h, backgroundColor: DIM }} />
          <View pointerEvents="none" style={{ position: 'absolute', left: box.x + box.w, right: 0, top: box.y, height: box.h, backgroundColor: DIM }} />
          {/* Spotlight ring */}
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: box.x,
              top: box.y,
              width: box.w,
              height: box.h,
              borderRadius: box.radius,
              borderWidth: 3,
              borderColor: 'rgba(255,255,255,0.9)',
            }}
          />
        </>
      ) : (
        <View pointerEvents="none" style={[StyleSheet.absoluteFill, { backgroundColor: DIM }]} />
      )}

      {/* Callout card */}
      <View style={{ position: 'absolute', left: spacing.screen, right: spacing.screen, ...cardWrap }}>
        <View style={{ backgroundColor: colors.card, borderRadius: radius.card, padding: spacing.lg, gap: spacing.sm }}>
          <AppText variant="headlineSm" color={colors.primary}>
            {t(step.titleKey)}
          </AppText>
          <AppText variant="bodyLg" color={colors.textSecondary}>
            {t(step.bodyKey)}
          </AppText>

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.xs }}>
            {STEPS.map((s, i) => (
              <View
                key={s.titleKey}
                style={{
                  width: i === idx ? 22 : 8,
                  height: 8,
                  borderRadius: radius.full,
                  backgroundColor: i === idx ? colors.primary : colors.outlineVariant,
                }}
              />
            ))}
          </View>

          <PrimaryButton label={isLast ? t('tutorial.done') : t('tutorial.next')} onPress={next} />
          {!isLast && (
            <Pressable accessibilityRole="button" onPress={close} style={{ minHeight: 40, justifyContent: 'center' }}>
              <AppText variant="labelLg" color={colors.outline} style={{ textAlign: 'center' }}>
                {t('tutorial.skip')}
              </AppText>
            </Pressable>
          )}
        </View>
      </View>
    </Modal>
  );
}
